import { useMemo, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import { Badge, Card, Tabs } from '@nexora/design-system';

/* ── Contrato ──────────────────────────────────────────────────────────────
 * `GET /api/v1/orders/:id` → `SalesOrderResponseDto`
 *   (sales/presentation/http/dto/sales.dto.ts:234). Campos usados aqui:
 *   id, number, customerId, customerName, status, items[{id, productId,
 *   description, quantity, unitValue, discount, discountType, taxValues[],
 *   total, type}], subtotal, discountTotal, taxTotal, grandTotal, conditions,
 *   cfop, paymentTerms, fiscalDocumentId, createdAt, financial { dueDate }.
 *
 * Aba Financeira — o que o servidor publica:
 *   `financial { dueDate }` (SalesOrderFinancialResponseDto) + `paymentTerms`
 *   + `discount total` do pedido. O protótipo mostra um cronograma de
 *   parcelas (Due Date × Valor) que o backend NÃO publica por parcela —
 *   a tela exibe o vencimento único real e o total, sem inventar parcelas.
 *
 * Aba Fiscal — o servidor publica:
 *   `cfop`, `taxTotal`, `taxValues` por item e `fiscalDocumentId`. A NF-e
 *   (chave/Número) não é publicada no detalhe: só o id do documento fiscal.
 */

interface OrderItemDetail {
  id: string;
  productId: string;
  description: string;
  quantity: number;
  unitValue: number;
  discount: number;
  discountType: string;
  taxValues: Record<string, number>;
  total: number;
  type: string;
}

interface OrderDetail {
  id: string;
  number: string;
  customerId: string;
  customerName: string | null;
  status: string;
  items: OrderItemDetail[];
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  grandTotal: number;
  conditions: string;
  cfop: string;
  paymentTerms: string;
  fiscalDocumentId: string | null;
  createdAt: string;
  financial: { dueDate: string | null } | null;
}

const STATUS_VARIANT: Record<string, 'info' | 'warning' | 'success' | 'error' | 'neutral'> = {
  DRAFT: 'neutral',
  CONFIRMED: 'info',
  INVOICED: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Rascunho',
  CONFIRMED: 'Confirmado',
  INVOICED: 'Faturado',
  COMPLETED: 'Concluído',
  CANCELLED: 'Cancelado',
};

export function SalesOrderFinFiscalPage(): JSX.Element {
  const params = useParams({ strict: false }) as { saleId?: string };
  const orderId = params.saleId;
  const [tab, setTab] = useState('financeira');

  const { data: order, isLoading, error } = useQuery({
    queryKey: ['sales', 'orders', orderId ?? 'sem-id'],
    queryFn: async () => apiClient.get<OrderDetail>(`/orders/${orderId}`),
    enabled: !!orderId,
  });

  /* Tributos por item, agrupados para a aba fiscal. */
  const taxesByItem = useMemo(() => {
    if (!order) return [];
    return order.items
      .map((item) => ({
        id: item.id,
        productId: item.productId,
        description: item.description,
        taxes: Object.entries(item.taxValues ?? {}).map(([name, value]) => ({ name, value })),
        taxSum: Object.values(item.taxValues ?? {}).reduce((s, v) => s + v, 0),
      }))
      .filter((row) => row.taxSum > 0);
  }, [order]);

  return (
    <div className="sales-finfiscal-page" style={{ padding: '24px' }}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-md mb-lg">
        <div>
          <div className="flex items-center gap-sm flex-wrap">
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">
              {order?.number ? `Pedido ${order.number}` : 'Pedido de venda'}
            </h1>
            {order ? (
              <Badge variant={STATUS_VARIANT[order.status] ?? 'neutral'}>
                {STATUS_LABEL[order.status] ?? order.status}
              </Badge>
            ) : null}
          </div>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Integração financeira e fiscal do pedido.
          </p>
        </div>
      </div>

      {isLoading ? (
        <Card>
          <p className="font-body-md text-body-md text-on-surface-variant">Carregando pedido…</p>
        </Card>
      ) : error || !order ? (
        <Card>
          <p className="font-body-md text-body-md text-error mb-md" role="alert">
            {error ? (error as Error).message ?? 'Pedido não encontrado' : 'Pedido não informado.'}
          </p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-lg">
            {/* ── Coluna principal ── */}
            <div className="lg:col-span-2 flex flex-col gap-lg">
              <Card>
                <h3 className="font-headline-sm text-headline-sm text-primary mb-md">
                  Customer Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-md">
                  <div>
                    <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                      Cliente
                    </span>
                    <p className="font-body-md text-body-md text-on-surface">
                      {order.customerName ?? <code>{order.customerId.slice(0, 8)}</code>}
                    </p>
                  </div>
                  <div>
                    <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                      Emissão
                    </span>
                    <p className="font-body-md text-body-md text-on-surface">
                      {formatDate(order.createdAt)}
                    </p>
                  </div>
                  <div>
                    <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                      Condição de pagamento
                    </span>
                    <p className="font-body-md text-body-md text-on-surface">
                      {order.paymentTerms || '—'}
                    </p>
                  </div>
                  <div>
                    <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                      Referência
                    </span>
                    <p className="font-body-md text-body-md text-on-surface">
                      {order.conditions || '—'}
                    </p>
                  </div>
                </div>
              </Card>

              <Card>
                <h3 className="font-headline-sm text-headline-sm text-primary mb-md">Line Items</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-surface-container-low border-b border-outline-variant font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">
                        <th className="p-sm font-medium">Produto</th>
                        <th className="p-sm font-medium text-right">Qtd</th>
                        <th className="p-sm font-medium text-right">V. Unit</th>
                        <th className="p-sm font-medium text-right">Desc.</th>
                        <th className="p-sm font-medium text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="font-body-md text-body-md text-on-surface">
                      {order.items.map((item) => (
                        <tr
                          key={item.id}
                          className="border-b border-surface-container hover:bg-surface-container-lowest transition-colors"
                        >
                          <td className="p-sm">
                            <span className="block font-label-md text-label-md text-primary">
                              {item.productId.slice(0, 8)}
                            </span>
                            <span className="block font-body-sm text-body-sm text-on-surface-variant">
                              {item.description}
                            </span>
                          </td>
                          <td className="p-sm text-right">{item.quantity}</td>
                          <td className="p-sm text-right">{formatCurrency(item.unitValue)}</td>
                          <td className="p-sm text-right text-error">
                            {item.discount > 0 ? `- ${formatCurrency(item.discount)}` : '—'}
                          </td>
                          <td className="p-sm text-right font-bold">{formatCurrency(item.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* ── Abas de integração ── */}
              <Card>
                <Tabs
                  tabs={[
                    { key: 'financeira', label: 'Integração Financeira' },
                    { key: 'fiscal', label: 'Integração Fiscal' },
                  ]}
                  value={tab}
                  onChange={setTab}
                  variant="underline"
                  aria-label="Integração financeira e fiscal"
                />

                {tab === 'financeira' ? (
                  <div className="mt-md space-y-md">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-md">
                      <div>
                        <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                          Vencimento
                        </span>
                        <p className="font-body-md text-body-md text-on-surface">
                          {order.financial?.dueDate
                            ? formatDate(order.financial.dueDate)
                            : '—'}
                        </p>
                      </div>
                      <div>
                        <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                          Condição
                        </span>
                        <p className="font-body-md text-body-md text-on-surface">
                          {order.paymentTerms || '—'}
                        </p>
                      </div>
                      <div>
                        <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                          Valor a receber
                        </span>
                        <p className="font-body-md text-body-md text-on-surface">
                          {formatCurrency(order.grandTotal)}
                        </p>
                      </div>
                    </div>

                    {order.financial?.dueDate ? (
                      <div className="border border-outline-variant rounded overflow-hidden">
                        <table className="w-full text-left border-collapse">
                          <thead className="bg-surface-container-low">
                            <tr className="border-b border-outline-variant font-label-sm text-label-sm text-on-surface-variant">
                              <th className="p-xs font-medium">Vencimento</th>
                              <th className="p-xs font-medium text-right">Valor</th>
                            </tr>
                          </thead>
                          <tbody className="font-body-sm text-body-sm text-on-surface">
                            <tr>
                              <td className="p-xs border-b border-surface-container">
                                {formatDate(order.financial.dueDate)}
                              </td>
                              <td className="p-xs border-b border-surface-container text-right">
                                {formatCurrency(order.grandTotal)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="font-body-sm text-body-sm text-on-surface-variant">
                        Nenhum vencimento registrado para este pedido.
                      </p>
                    )}

                    <div className="bg-surface-container-low rounded p-md">
                      <p className="font-body-sm text-body-sm text-on-surface-variant">
                        O backend publica apenas o vencimento único
                        (`financial.dueDate`); o cronograma de parcelas do
                        protótipo não existe no contrato e não foi inventado.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mt-md space-y-md">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-md">
                      <div>
                        <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                          CFOP
                        </span>
                        <p className="font-body-md text-body-md text-on-surface">
                          {order.cfop || '—'}
                        </p>
                      </div>
                      <div>
                        <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                          Tributos estimados
                        </span>
                        <p className="font-body-md text-body-md text-on-surface">
                          {formatCurrency(order.taxTotal)}
                        </p>
                      </div>
                      <div>
                        <span className="block font-label-md text-label-md text-on-surface-variant mb-xs">
                          Documento fiscal
                        </span>
                        <p className="font-body-md text-body-md text-on-surface">
                          {order.fiscalDocumentId ? (
                            <code>{order.fiscalDocumentId}</code>
                          ) : (
                            'não faturado'
                          )}
                        </p>
                      </div>
                    </div>

                    {taxesByItem.length > 0 ? (
                      <div className="border border-outline-variant rounded overflow-hidden">
                        <table className="w-full text-left border-collapse">
                          <thead className="bg-surface-container-low">
                            <tr className="border-b border-outline-variant font-label-sm text-label-sm text-on-surface-variant">
                              <th className="p-xs font-medium">Produto</th>
                              <th className="p-xs font-medium text-right">Tributos</th>
                              <th className="p-xs font-medium text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody className="font-body-sm text-body-sm text-on-surface">
                            {taxesByItem.map((row) => (
                              <tr key={row.id} className="border-b border-surface-container">
                                <td className="p-xs">
                                  <span className="block font-label-md text-label-md text-primary">
                                    {row.productId.slice(0, 8)}
                                  </span>
                                  <span className="block font-body-sm text-body-sm text-on-surface-variant">
                                    {row.description}
                                  </span>
                                </td>
                                <td className="p-xs text-right">
                                  {row.taxes.map((t) => (
                                    <span key={t.name} className="block">
                                      {t.name}: {formatCurrency(t.value)}
                                    </span>
                                  ))}
                                </td>
                                <td className="p-xs text-right font-bold">
                                  {formatCurrency(row.taxSum)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="font-body-sm text-body-sm text-on-surface-variant">
                        Nenhum tributo por item publicado neste pedido.
                      </p>
                    )}

                    <div className="bg-surface-container-low rounded p-md">
                      <p className="font-body-sm text-body-sm text-on-surface-variant">
                        CFOP, totais de tributo e impostos por item vêm do
                        contrato real (`cfop`, `taxTotal`, `taxValues`).
                      </p>
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {/* ── Coluna lateral: totais ── */}
            <div className="flex flex-col gap-lg">
              <Card>
                <h3 className="font-headline-sm text-headline-sm text-primary mb-md">Totais</h3>
                <div className="space-y-sm">
                  <div className="flex justify-between font-body-md text-body-md text-on-surface-variant">
                    <span>Subtotal</span>
                    <span>{formatCurrency(order.subtotal)}</span>
                  </div>
                  <div className="flex justify-between font-body-md text-body-md text-error">
                    <span>Desconto</span>
                    <span>- {formatCurrency(order.discountTotal)}</span>
                  </div>
                  {order.taxTotal > 0 ? (
                    <div className="flex justify-between font-body-md text-body-md text-on-surface-variant">
                      <span>Tributos</span>
                      <span>+ {formatCurrency(order.taxTotal)}</span>
                    </div>
                  ) : null}
                  <div className="flex justify-between font-headline-md text-headline-md text-on-surface pt-sm border-t border-surface-container">
                    <span>Total</span>
                    <span>{formatCurrency(order.grandTotal)}</span>
                  </div>
                </div>
              </Card>

              <Card>
                <h3 className="font-headline-sm text-headline-sm text-primary mb-md">
                  Documento Fiscal
                </h3>
                {order.fiscalDocumentId ? (
                  <div className="space-y-xs">
                    <div className="flex justify-between items-center">
                      <span className="font-label-md text-label-md text-on-surface-variant">
                        Status
                      </span>
                      <Badge variant="success">Faturado</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-label-md text-label-md text-on-surface-variant">
                        Documento
                      </span>
                      <code className="font-body-sm text-body-sm text-on-surface">
                        {order.fiscalDocumentId}
                      </code>
                    </div>
                  </div>
                ) : (
                  <p className="font-body-sm text-body-sm text-on-surface-variant">
                    Pedido ainda não faturado. A NF-e é emitida na lista de
                    pedidos de venda.
                  </p>
                )}
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}